/**
 * Small presentational pieces shared by every page.
 *
 * None of them fetch, none of them know about routes, and all of them take their text as props so
 * that translation happens at the call site where the key is meaningful.
 */

import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { AlertTriangle, Check, Copy } from 'lucide-react'
import { colorFor, initials } from '../lib/format'
import type { ActivityState, CostAccuracy } from '../lib/types'

export function Panel({
  title,
  subtitle,
  actions,
  children,
  flush,
}: {
  title?: ReactNode
  /** A line under the title, for when the title alone would be read as something it is not. */
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  flush?: boolean
}) {
  return (
    <section className="panel">
      {(title || actions) && (
        <header className="panel__header">
          {title && (
            <div className="stack" style={{ gap: 2, minWidth: 0 }}>
              <h2 className="panel__title">{title}</h2>
              {subtitle && <span className="tiny muted">{subtitle}</span>}
            </div>
          )}
          <div className="spacer" />
          {actions}
        </header>
      )}
      <div className={flush ? 'panel__body panel__body--flush' : 'panel__body'}>{children}</div>
    </section>
  )
}

export function Stat({
  label,
  value,
  foot,
  tone,
  icon,
}: {
  label: string
  value: ReactNode
  foot?: ReactNode
  tone?: 'accent' | 'ok' | 'warn' | 'danger'
  icon?: ReactNode
}) {
  const toneClass = tone ? ` stat__${tone}` : ''
  return (
    <div className="stat">
      <div className="stat__label">
        {icon}
        {label}
      </div>
      <div className={`stat__value${toneClass}`}>{value}</div>
      {foot && <div className="stat__foot">{foot}</div>}
    </div>
  )
}

export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode
  tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info' | 'purple' | 'demo'
  title?: string
}) {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      {children}
    </span>
  )
}

/** Maps an activity state to its tone, so the mapping exists once. */
export function toneForState(state: ActivityState): 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info' | 'purple' {
  switch (state) {
    case 'Blocked':
      return 'danger'
    case 'Testing':
      return 'warn'
    case 'Completed':
      return 'ok'
    case 'Coding':
      return 'accent'
    case 'Analyzing':
      return 'info'
    case 'Planning':
    case 'Reviewing':
      return 'purple'
    default:
      return 'neutral'
  }
}

export function ProgressBar({
  value,
  tone,
  size = 'md',
  showValue = false,
  label,
}: {
  value: number
  tone?: 'ok' | 'warn' | 'danger' | 'idle'
  size?: 'sm' | 'md' | 'lg'
  showValue?: boolean
  label?: string
}) {
  const clamped = Math.max(0, Math.min(100, value))
  const sizeClass = size === 'md' ? '' : ` progress--${size}`
  const toneClass = tone ? ` progress__fill--${tone}` : ''

  const bar = (
    <div
      className={`progress${sizeClass}`}
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
    >
      <div className={`progress__fill${toneClass}`} style={{ width: `${clamped}%` }} />
    </div>
  )

  if (!showValue) return bar

  return (
    <div className="progress-row">
      {bar}
      <span className="progress-row__value">{Math.round(clamped)}%</span>
    </div>
  )
}

export function Avatar({
  name,
  color,
  size = 'md',
}: {
  name: string
  color?: string | null
  size?: 'sm' | 'md' | 'lg'
}) {
  const sizeClass = size === 'md' ? '' : ` avatar--${size}`
  return (
    <span
      className={`avatar${sizeClass}`}
      style={{ background: color ?? colorFor(name) }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  )
}

export function LiveDot({ state }: { state: 'live' | 'stale' | 'off' }) {
  const modifier = state === 'live' ? '' : ` live-dot--${state}`
  return <span className={`live-dot${modifier}`} aria-hidden="true" />
}

export function EmptyState({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {body && <p className="empty__body">{body}</p>}
      {action}
    </div>
  )
}

export function Skeleton({ height = 20, width = '100%' }: { height?: number | string; width?: number | string }) {
  return <div className="skeleton" style={{ height, width }} aria-hidden="true" />
}

export function ErrorBanner({ message, onRetry, retryLabel }: { message: string; onRetry?: () => void; retryLabel?: string }) {
  return (
    <div className="toast toast--danger" role="alert">
      <AlertTriangle size={16} />
      <div className="stack stack--tight">
        <span>{message}</span>
        {onRetry && (
          <button type="button" className="btn btn--sm" onClick={onRetry}>
            {retryLabel ?? 'Retry'}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * A cost figure with its accuracy attached.
 *
 * The label is not decoration. An estimated total and a billed total are different claims, and a
 * product that renders them identically is inviting someone to quote a guess in a budget meeting.
 */
/**
 * A cost, with its accuracy carried by the hint rather than by a badge beside the number.
 *
 * There was a badge here, and it did not fit: at the size this number is set, "US$ 116,50" plus a
 * pill overflowed the card and the pill was clipped to "ESTIM…". It was also saying a second time
 * what the line directly beneath it already said in full. The caller labels the accuracy; this
 * renders the figure.
 */
export function CostValue({
  value,
  accuracy,
  formatted,
  unavailableLabel,
  hint,
}: {
  value: number | null
  accuracy: CostAccuracy
  formatted: string
  unavailableLabel: string
  hint: string
}) {
  if (accuracy === 'Unavailable' || value === null) {
    return (
      <span className="muted" title={hint}>
        {unavailableLabel}
      </span>
    )
  }

  return (
    <span title={hint}>{formatted}</span>
  )
}

export function CopyButton({ text, copyLabel, copiedLabel }: { text: string; copyLabel: string; copiedLabel: string }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timer = setTimeout(() => setCopied(false), 1800)
    return () => clearTimeout(timer)
  }, [copied])

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
    } catch {
      // Clipboard access can be refused. The text is selectable either way, so there is nothing
      // useful to say beyond not pretending it worked.
    }
  }

  return (
    <button type="button" className="btn btn--sm btn--ghost" onClick={copy}>
      {copied ? <Check size={13} /> : <Copy size={13} />}
      {copied ? copiedLabel : copyLabel}
    </button>
  )
}

export function CodeBlock({ code, copyLabel, copiedLabel }: { code: string; copyLabel: string; copiedLabel: string }) {
  return (
    <div className="code">
      <div className="code__copy">
        <CopyButton text={code} copyLabel={copyLabel} copiedLabel={copiedLabel} />
      </div>
      {code}
    </div>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: Array<{ value: T; label: string }>
  onChange: (value: T) => void
}) {
  return (
    <div className="segmented" role="tablist">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={option.value === value}
          className={`segmented__item${option.value === value ? ' segmented__item--active' : ''}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
